import qs from 'qs';
import ApiService from '../../common/services/ApiService';

const api = new ApiService();

// The filter shape produced by the Overview dashboard's filter bar.
export interface DashFilters {
  division_id?: string;
  department_ids?: string[];
  employment_type_id?: string;
  year?: string;
}

// A ransack filter hash, e.g. { effective_division_id_eq: '3' }.
export type RansackParams = Record<string, unknown>;

export interface DashWidget {
  type: string;
  [key: string]: unknown;
}

export interface DashResponse {
  widgets: DashWidget[];
}

export interface ActionQueueCheck {
  // True when any of the counts below is positive.
  unactioned: boolean;
  counts: {
    vcms: number;
    profiles: number;
    registrations: number;
    point_requests: number;
  };
}

const buildDashQueryString = (filters: DashFilters = {}): string => {
  const q: RansackParams = {};

  if (filters.division_id && filters.division_id !== 'all') {
    q.effective_division_id_eq = filters.division_id;
  }

  const departmentIds = (filters.department_ids || []).filter((id) => id && id !== 'all');
  if (departmentIds.length > 0) {
    q.department_id_in = departmentIds;
  }

  if (filters.employment_type_id && filters.employment_type_id !== 'all') {
    q.employment_type_id_eq = filters.employment_type_id;
  }

  const params: { q: RansackParams; year?: string } = { q };

  if (filters.year) {
    params.year = filters.year;
  }

  return qs.stringify(params, { arrayFormat: 'brackets' });
};

// Queues the overview export job on the backend, which emails the report to the
// current user. `q` is a ransack filter hash (see toPassportTableFilters) - the
// job feeds it straight into the passport report, so it takes the same shape as
// the Educator table's filters rather than the dash query string above. The API
// takes `year` separately, `date_eq` is only for the passport report.
export const exportDash = async (q: RansackParams = {}, year?: string) => {
  try {
    const response = await api.post('/api/v1/overview_dashboard/export', { q, year });

    if (response.status !== 202 && response.status !== 200) {
      throw new Error(`Export request failed with status ${response.status}`);
    }

    return response.json;
  } catch (error) {
    console.error('Error requesting dash export:', error);
    throw error;
  }
};

// Whether the signed-in user has anything waiting in the Action Queue, scoped by
// their ability the same way the queue's own panels are.
export const getActionQueueCheck = async (): Promise<ActionQueueCheck | undefined> => {
  try {
    const check = await api.query('/api/v1/action_queue/check');
    return check.data;
  } catch (error) {
    console.error('Error fetching action queue check:', error);
  }
};

export const getDash = async (filters?: DashFilters): Promise<DashResponse | undefined> => {
  try {
    const queryString = buildDashQueryString(filters);
    const url = queryString
      ? `/api/v1/overview_dashboard?${queryString}`
      : `/api/v1/overview_dashboard`;
    const dash = await api.query(url);
    return dash.data;
  } catch (error) {
    console.error('Error fetching dash:', error);
  }
};
